// filepath: apps/web/src/operator/useOperatorToken.ts
//
// FR-UI-OPS-1 AC-2 — operator-token store (in-memory + sessionStorage).
//
// The platform operator console authenticates each request via
// `Authorization: Bearer <PLATFORM_ADMIN_TOKEN>` (validated
// server-side by StaticOperatorAuth). The token is held on the
// SPA in a module-scoped, in-memory store AND mirrored to
// sessionStorage so the operator doesn't have to re-enter the
// token on every page refresh (ISS-223, operator-filed 2026-10-07).
//
// SECURITY (relaxation of INV-5 per operator review, ISS-223):
//   The original INV-5 ban on sessionStorage persists for the OIDC
//   user-token surface (see apps/web/src/auth/oidcConfig.ts
//   INV-token-storage — in-memory only). For the OPERATOR token
//   surface the supervisor approved a sessionStorage relaxation:
//   the token is scoped to a namespaced key (`ascoa.operator.token`),
//   is automatically cleared by the browser on tab close
//   (sessionStorage is tab-scoped), and is never written to
//   localStorage / cookies / the URL. The threat-model impact is
//   small: an XSS that can read sessionStorage could already read
//   the token from the page's JS heap (the in-memory store), and
//   the token is also rendered in the password input field while
//   the user is typing it. Persisting to sessionStorage changes
//   the convenience-vs-threat-model trade-off in the operator's
//   favor. This is the documented ISS-223 relaxation.
//
// IMPLEMENTATION NOTE: this module deliberately uses
// React's `useSyncExternalStore` over a hand-rolled subscriber
// pattern instead of pulling in a state-library dependency
// (zustand is not bundled in this app). The store is a tiny
// module-scoped { state, listeners } object — the same pattern
// redux / zustand use internally, just exposed directly.

import { useSyncExternalStore } from "react";

/** sessionStorage key — namespaced, scoped to the operator surface. */
const STORAGE_KEY = "ascoa.operator.token";

/**
 * Read the persisted token from sessionStorage. Returns null on
 * any failure (SSR / no window / storage disabled / corrupted
 * value). The error path is silent because the gate re-prompts
 * the user in either case — there is no recovery action.
 */
function readPersistedToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the token to sessionStorage. Best-effort: storage
 * failures (quota / disabled / SecurityError) are swallowed
 * because the in-memory store remains the source of truth for
 * the page lifetime. On page refresh we still re-read from
 * sessionStorage; if a write failed here, the token is gone
 * and the gate re-prompts.
 */
function writePersistedToken(token: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (token === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, token);
    }
  } catch {
    // best-effort; in-memory store still authoritative for page
    // lifetime.
  }
}

/**
 * The in-memory store. Module-scoped so all consumers share the
 * same token. NOT exported — only the `useOperatorToken` hook and
 * the `setOperatorToken` / `clearOperatorToken` actions are
 * exposed.
 *
 * Initial value is read from sessionStorage at module load so
 * that a page refresh (which clears the JS heap) rehydrates the
 * token without prompting the operator.
 */
const store: {
  token: string | null;
  listeners: Set<() => void>;
} = {
  token: readPersistedToken(),
  listeners: new Set(),
};

/**
 * Subscribe to store changes. Returns an unsubscribe function.
 * Used by `useSyncExternalStore` to subscribe React components.
 */
function subscribe(listener: () => void): () => void {
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

/**
 * Stable snapshot accessor. `useSyncExternalStore` requires the
 * snapshot to be referentially stable when the store is unchanged
 * (otherwise it re-renders on every render). Returning a frozen
 * empty-object placeholder that is reused does the trick.
 */
const UNCHANGED_TOKEN: Readonly<{ token: string | null }> = Object.freeze({
  token: null,
});
/**
 * Snapshot cache. `useSyncExternalStore` compares snapshots by
 * reference; if `getSnapshot()` returns a fresh object on every
 * call, React sees "the store changed" every render and loops
 * ("Maximum update depth exceeded"). We cache the last snapshot
 * by token value so the same `store.token` value yields the same
 * object reference across calls. When the token changes (or is
 * cleared back to `null`), we allocate a fresh frozen object.
 */
let lastSnapshotToken: string | null | undefined = undefined;
let lastSnapshot: Readonly<{ token: string | null }> = UNCHANGED_TOKEN;
function getSnapshot(): Readonly<{ token: string | null }> {
  // `Object.freeze` makes the object immutable; consumers must
  // not mutate `snapshot.token`. We return a fresh wrapper only
  // when the token changes so React's structural-sharing check
  // sees referential stability in the no-change path.
  if (store.token === lastSnapshotToken) return lastSnapshot;
  lastSnapshotToken = store.token;
  lastSnapshot =
    store.token === null
      ? UNCHANGED_TOKEN
      : Object.freeze({ token: store.token });
  return lastSnapshot;
}

/**
 * Internal snapshot type. Not exported directly — callers go
 * through the `useOperatorToken()` hook.
 */
export interface OperatorTokenSnapshot {
  token: string | null;
}

/**
 * Read the current operator token (and a stable setter pair). The
 * returned `set` and `clear` references are stable across renders.
 *
 * IMPORTANT: the token value is NEVER rendered to the DOM by
 * `OperatorTokenPrompt` (the input is `type="password"`, no value
 * echo). The set/clear functions are pure imperative mutations.
 */
export function useOperatorToken(): {
  token: string | null;
  set: (token: string) => void;
  clear: () => void;
} {
  const snapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => UNCHANGED_TOKEN,
  );
  return {
    token: snapshot.token,
    set,
    clear,
  };
}

/**
 * Set the operator token. Called from `OperatorTokenPrompt` on
 * submit. The token is mutated synchronously, then listeners are
 * notified AND the value is persisted to sessionStorage so the
 * gate survives a page refresh (ISS-223).
 *
 * Empty / whitespace-only values are treated as a clear() — the
 * gate re-prompts rather than storing a falsy token.
 */
function set(token: string): void {
  const trimmed = token.trim();
  if (trimmed.length === 0) {
    clear();
    return;
  }
  if (store.token === trimmed) return;
  store.token = trimmed;
  // Persist AFTER the in-memory write so a sessionStorage failure
  // does not corrupt the page-lifetime store. The relaxation
  // scope is documented in the file header.
  writePersistedToken(trimmed);
  notify();
}

/**
 * Clear the operator token. Called from `useOperatorSummary` on
 * HTTP 401, and internally by `set("")`. After clearing, the
 * gate re-renders the token prompt AND the sessionStorage entry
 * is removed so the next refresh does NOT rehydrate the stale
 * token.
 */
function clear(): void {
  if (store.token === null) return;
  store.token = null;
  writePersistedToken(null);
  notify();
}

function notify(): void {
  for (const listener of store.listeners) {
    listener();
  }
}

/**
 * Test-only escape hatch. Resets the store to `null` and clears
 * all listeners. Used by test files to ensure the in-memory store
 * does not leak across tests. MUST NOT be called from production
 * source.
 */
export function __resetOperatorTokenForTests(): void {
  store.token = null;
  store.listeners.clear();
  // Also clear sessionStorage so the next mount of the module
  // starts from a clean slate (mirrors the no-leak intent).
  writePersistedToken(null);
}

/**
 * Test-only escape hatch. Sets the operator token directly,
 * bypassing the prompt UI. Used by tests that need a token
 * pre-set before mounting the component under test. MUST NOT be
 * called from production source — production goes through the
 * OperatorTokenPrompt UI which exercises the input value →
 * trimmed token → set() chain end-to-end (preserves INV-5: no
 * programmatic setter in the production bundle).
 */
export function __setOperatorTokenForTests(token: string): void {
  const trimmed = token.trim();
  if (trimmed.length === 0) {
    store.token = null;
  } else {
    store.token = trimmed;
  }
  writePersistedToken(trimmed.length === 0 ? null : trimmed);
  for (const listener of store.listeners) {
    listener();
  }
}
